import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Grid, Box, FormControl, Select, MenuItem, Chip, Typography } from "@mui/material";

import {
  Refresh,
  AccessTime,
  LocalParking,
  CheckCircle,
  People,
  Warning as WarningIcon,
  Add,
  Business,
  Store,
} from "@mui/icons-material";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";

// CORRECCIÓN: Importar desde authCommerce
import { switchCommerce } from "modules/copropiedades/services/authService";

const HeaderAdmin = ({
  onRefresh,
  loading = false,
  usuariosStats = null,
  onAddClick = null,
  commerceName = null,
  commerces = [],
  onCommerceChange = null,
  hasCreatePermission = false,
}) => {
  const [selectedCommerce, setSelectedCommerce] = useState(commerceName || "");
  const [availableCommerces, setAvailableCommerces] = useState(commerces || []);
  const [propertyName, setPropertyName] = useState(commerceName || "");
  const [currentTime, setCurrentTime] = useState("");

  const formatTime = () => {
    const now = new Date();
    return now.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  useEffect(() => {
    setCurrentTime(formatTime());
    const interval = setInterval(() => {
      setCurrentTime(formatTime());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Obtener nombre de la propiedad y comercios disponibles
  useEffect(() => {
    const loadCommerceData = () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user") || "{}");

        const name =
          commerceName ||
          userData.commerceName ||
          userData.commerce_name ||
          userData.user?.commerceName ||
          userData.user?.commerce_name ||
          userData.propertyName ||
          userData.property_name ||
          "";

        setPropertyName(name);
        setSelectedCommerce(name);

        let commercesData = commerces;
        if (commerces.length === 0) {
          const storedCommerces = userData.commerces || userData.availableCommerces || [];
          if (storedCommerces.length > 0) {
            commercesData = storedCommerces;
          }
        }
        setAvailableCommerces(commercesData);
      } catch (error) {
        console.error("❌ Error obteniendo datos del comercio:", error);
      }
    };

    loadCommerceData();
  }, [commerceName, commerces]);

  const handleCommerceChange = (event) => {
    const newCommerceName = event.target.value;
    setSelectedCommerce(newCommerceName);

    const selected = availableCommerces.find((c) => c.name === newCommerceName);

    if (selected) {
      switchCommerce(selected.id, selected.name);
      setPropertyName(selected.name);

      if (onCommerceChange) {
        onCommerceChange(selected.id, selected.name);
      }
    }
  };

  const totalProperties = availableCommerces.length;

  return (
    <MDBox position="relative" mb={0}>
      <Card
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <MDBox
          p={3}
          sx={{
            backgroundColor: "#0d47a1",
            background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={5}>
              <MDBox display="flex" alignItems="center" gap={2}>
                <LocalParking sx={{ color: "#4cc9f0", fontSize: 32 }} />
                <Box>
                  <MDTypography variant="h5" color="white" fontWeight="bold">
                    Administración Usuarios Parqueadero
                  </MDTypography>
                  <MDTypography variant="caption" color="white" display="block">
                    Gestión de usuarios del parqueadero
                  </MDTypography>
                  {propertyName && (
                    <MDBox display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                      <Business sx={{ color: "#4cc9f0", fontSize: 14 }} />
                      <MDTypography variant="body2" color="white" fontWeight="bold">
                        {propertyName}
                      </MDTypography>
                    </MDBox>
                  )}
                </Box>
              </MDBox>
            </Grid>

            <Grid item xs={12} md={7}>
              <MDBox
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
              >
                {totalProperties > 1 && (
                  <Box
                    sx={{
                      minWidth: 280,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{
                        color: "rgba(255,255,255,0.9)",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        ml: 0.5,
                      }}
                    >
                      <Store sx={{ fontSize: 15 }} />
                      Propiedad
                    </Box>

                    <FormControl size="small" fullWidth>
                      <Select
                        value={selectedCommerce}
                        onChange={handleCommerceChange}
                        displayEmpty
                        sx={{
                          height: 42,
                          backgroundColor: "#ffffff",
                          borderRadius: "8px",
                          color: "#263238",
                          fontWeight: 500,
                          "& .MuiSelect-select": {
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 12px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(255,255,255,0.8)",
                            borderWidth: "1px",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ffffff",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#4cc9f0",
                            borderWidth: "2px",
                          },
                          "& .MuiSvgIcon-root": {
                            color: "#0d47a1",
                          },
                        }}
                        renderValue={(selected) => {
                          const commerce = availableCommerces.find((c) => c.name === selected);

                          if (!commerce) {
                            return (
                              <Typography sx={{ color: "#757575", fontSize: "0.85rem" }}>
                                Seleccionar propiedad
                              </Typography>
                            );
                          }

                          return (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              width="100%"
                              gap={1}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ minWidth: 0, overflow: "hidden" }}
                              >
                                <Store
                                  sx={{
                                    fontSize: 18,
                                    color: "#0d47a1",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                    color: "#263238",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {commerce.name}
                                </Typography>
                              </Box>

                              <Chip
                                label={`ID: ${commerce.id}`}
                                size="small"
                                sx={{
                                  height: 22,
                                  borderRadius: "5px",
                                  backgroundColor: "#e3f2fd",
                                  color: "#0d47a1",
                                  fontWeight: 600,
                                  fontSize: "0.65rem",
                                  flexShrink: 0,
                                  "& .MuiChip-label": { px: 0.8 },
                                }}
                              />
                            </Box>
                          );
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              mt: 0.5,
                              borderRadius: "8px",
                              boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                              maxHeight: 300,
                            },
                          },
                        }}
                      >
                        {availableCommerces.map((commerce) => (
                          <MenuItem
                            key={commerce.id}
                            value={commerce.name}
                            sx={{
                              minHeight: 48,
                              borderRadius: "6px",
                              mx: 0.5,
                              my: 0.25,
                              "&.Mui-selected": {
                                backgroundColor: "#e3f2fd",
                              },
                              "&.Mui-selected:hover": {
                                backgroundColor: "#bbdefb",
                              },
                              "&:hover": {
                                backgroundColor: "#f5f9ff",
                              },
                            }}
                          >
                            <Box display="flex" alignItems="center" width="100%" gap={1}>
                              <Store sx={{ fontSize: 18, color: "#0d47a1" }} />
                              <Typography
                                sx={{
                                  flex: 1,
                                  fontSize: "0.85rem",
                                  fontWeight: 500,
                                  color: "#263238",
                                }}
                              >
                                {commerce.name}
                              </Typography>
                              <Chip
                                label={`ID: ${commerce.id}`}
                                size="small"
                                sx={{
                                  height: 22,
                                  borderRadius: "5px",
                                  backgroundColor: "#e3f2fd",
                                  color: "#0d47a1",
                                  fontSize: "0.65rem",
                                  fontWeight: 600,
                                  "& .MuiChip-label": { px: 0.8 },
                                }}
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                <MDTypography
                  variant="caption"
                  color="white"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <AccessTime fontSize="inherit" sx={{ mr: 0.5 }} />
                  {currentTime}
                </MDTypography>

                {hasCreatePermission && (
                  <MDButton
                    variant="contained"
                    color="white"
                    size="small"
                    onClick={onAddClick}
                    disabled={loading}
                    startIcon={<Add />}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      color: "#0d47a1",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(255,255,255,0.3)",
                        color: "rgba(255,255,255,0.5)",
                      },
                    }}
                  >
                    Nuevo Usuario
                  </MDButton>
                )}

                <MDButton
                  variant="contained"
                  color="white"
                  size="small"
                  onClick={onRefresh}
                  disabled={loading}
                  startIcon={<Refresh />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#0d47a1",
                    "&:hover": { backgroundColor: "#e3f2fd" },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(255,255,255,0.3)",
                      color: "rgba(255,255,255,0.5)",
                    },
                  }}
                >
                  {loading ? "Actualizando..." : "Actualizar"}
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {usuariosStats && (
          <MDBox
            p={2}
            sx={{
              backgroundColor: "#f0f4ff",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={10}>
                <MDBox display="flex" gap={4} flexWrap="wrap">
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <People fontSize="small" color="primary" />
                    <MDTypography variant="body2">
                      <strong>{usuariosStats.total || 0}</strong> Total Usuarios
                    </MDTypography>
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={1}>
                    <CheckCircle fontSize="small" color="success" />
                    <MDTypography variant="body2">
                      <strong>{usuariosStats.activeCount || 0}</strong> Activos
                    </MDTypography>
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={1}>
                    <WarningIcon fontSize="small" color="error" />
                    <MDTypography variant="body2">
                      <strong>{usuariosStats.expiredCount || 0}</strong> Expirados
                    </MDTypography>
                  </MDBox>

                  {totalProperties > 1 && (
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Store fontSize="small" color="info" />
                      <MDTypography variant="body2">
                        <strong>{totalProperties}</strong> Propiedades disponibles
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </Grid>
              <Grid item xs={12} md={2}>
                <MDBox display="flex" justifyContent="flex-end">
                  <MDTypography variant="caption" color="text.secondary">
                    <People fontSize="inherit" sx={{ mr: 0.5 }} />
                    Usuarios
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        )}
      </Card>
    </MDBox>
  );
};

HeaderAdmin.propTypes = {
  onRefresh: PropTypes.func,
  loading: PropTypes.bool,
  usuariosStats: PropTypes.object,
  onAddClick: PropTypes.func,
  commerceName: PropTypes.string,
  commerces: PropTypes.array,
  onCommerceChange: PropTypes.func,
  hasCreatePermission: PropTypes.bool,
};

HeaderAdmin.defaultProps = {
  onRefresh: null,
  loading: false,
  usuariosStats: null,
  onAddClick: null,
  commerceName: null,
  commerces: [],
  onCommerceChange: null,
  hasCreatePermission: false,
};

export default HeaderAdmin;
